import React from 'react'
import { CommonButton } from '@/components/common'
import { Box, Chip, Stack, Typography, useTheme } from '@mui/material'
import { ChevronLeftIcon, ChevronRightIcon } from '@/icons'

import { useDateScroll } from './useDateScroll'

const LoadingDots = () => (
  <Typography
    variant='caption'
    sx={{
      display: 'flex',
      gap: '2px',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: { xs: '1.5em', md: '1.46em' },
    }}
  >
    {[0, 1, 2].map(index => (
      <Box
        key={index}
        sx={{
          width: '3px',
          height: '3px',
          borderRadius: '50%',
          bgcolor: 'currentColor',
          animation: 'dotBounce 1.4s infinite ease-in-out',
          animationDelay: `${index * 0.16}s`,
          '@keyframes dotBounce': {
            '0%, 80%, 100%': {
              transform: 'translateY(0)',
            },
            '40%': {
              transform: 'translateY(-4px)',
            },
          },
        }}
      />
    ))}
  </Typography>
)

export const DateScroll = ({
  id,
  formData,
  dataMap,
  loadingDataMap,
  onSelect,
  onScroll,
}) => {
  const theme = useTheme()
  const {
    chips,
    isReady,
    selectedId,
    containerRef,
    leastDistanceDates,
    handleSelect,
    scrollByAmount,
  } = useDateScroll(id, formData, dataMap, onSelect, onScroll)

  return (
    <Box data-testid={id} sx={{ width: '100%' }}>
      <Typography variant='h6' sx={{ mb: 1 }}>
        When to Donate:
      </Typography>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}
      >
        <CommonButton
          id={`${id}-prev`}
          variant='primary'
          boxShadow={false}
          sx={{
            width: '28px',
            height: '28px',
            minHeight: '28px',
          }}
          iconOnly={<ChevronLeftIcon />}
          onClick={() => scrollByAmount('left')}
        />
        <Stack
          ref={containerRef}
          direction='row'
          sx={{
            gap: { xs: 0.8, md: 2 },
            flex: 1,
            minWidth: '0px',
            maxWidth: '100%',
            overflowX: 'auto',
            overflowY: 'visible',
            py: 0.5,
            my: -0.5,
            scrollbarWidth: 'none',
            scrollBehavior: isReady ? 'smooth' : 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {chips.map(chip => {
            const isActive = selectedId === chip.id
            const isClickable = !!chip.text2
            return (
              <Chip
                key={chip.id}
                id={`${id}-chip-${chip.id}`}
                data-testid={`${id}-chip-${chip.id}`}
                onClick={() => handleSelect(chip)}
                color={isActive ? 'primary' : 'default'}
                variant={isActive ? 'filled' : 'outlined'}
                sx={{
                  width: '106px',
                  minHeight: '56px',
                  height: 'auto',
                  borderRadius: '4px',
                  boxShadow: leastDistanceDates.includes(chip.id)
                    ? theme => `0 0 0 2px ${theme.palette.primary.main}`
                    : undefined,
                  ...(!isClickable &&
                    !isActive && {
                      backgroundColor: 'action.disabledBackground',
                      pointerEvents: 'none',
                    }),
                  ...(isClickable && {
                    '&:hover': {
                      background: theme.palette.primary.main + ' !important',
                      color: 'primary.contrastText',
                    },
                  }),
                }}
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      textAlign: 'center',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{ fontWeight: isActive ? 500 : 400, lineHeight: 1.2 }}
                    >
                      {chip.weekday || chip.text1}
                    </Typography>
                    {chip.dateDisplay && (
                      <Typography
                        variant='caption'
                        sx={{
                          fontWeight: isActive ? 500 : 400,
                          lineHeight: 1.2,
                        }}
                      >
                        {chip.dateDisplay}
                      </Typography>
                    )}
                    {loadingDataMap ? (
                      <LoadingDots />
                    ) : (
                      <Typography
                        variant='caption'
                        sx={{ fontWeight: isActive ? 500 : 400 }}
                      >
                        {chip.text2 || '--'}
                      </Typography>
                    )}
                  </Box>
                }
              />
            )
          })}
        </Stack>
        <CommonButton
          id={`${id}-next`}
          size='small'
          variant='primary'
          iconOnly={<ChevronRightIcon />}
          sx={{
            width: '28px',
            height: '28px',
            minHeight: '28px',
          }}
          onClick={() => scrollByAmount('right')}
        />
      </Box>
    </Box>
  )
}
